import React, { useState, useMemo } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Tooltip from '@mui/material/Tooltip';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import SearchIcon from '@mui/icons-material/Search';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import FolderIcon from '@mui/icons-material/Folder';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import HistoryIcon from '@mui/icons-material/History';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import CloseIcon from '@mui/icons-material/Close';
import RestoreIcon from '@mui/icons-material/Restore';
import useVoyagerStore from '../../stores/useVoyagerStore';
import { isOpenThread } from '../../stores/slices/threadsSlice';

function formatRelativeTime(isoString: string): string {
  const diff = Date.now() - new Date(isoString).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} minute${mins === 1 ? '' : 's'} ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} hour${hrs === 1 ? '' : 's'} ago`;
  const days = Math.floor(hrs / 24);
  if (days === 1) return 'yesterday';
  if (days < 7) return `${days} days ago`;
  return new Date(isoString).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export default function HistoryPage() {
  const savedThreads = useVoyagerStore((s) => s.savedThreads);
  const spaces = useVoyagerStore((s) => s.spaces);
  const loadThread = useVoyagerStore((s) => s.loadThread);
  const renameThread = useVoyagerStore((s) => s.renameThread);
  const deleteThread = useVoyagerStore((s) => s.deleteThread);
  const starThread = useVoyagerStore((s) => s.starThread);
  const closeThread = useVoyagerStore((s) => s.closeThread);
  const reopenThread = useVoyagerStore((s) => s.reopenThread);
  const addThreadToSpace = useVoyagerStore((s) => s.addThreadToSpace);
  const createSpace = useVoyagerStore((s) => s.createSpace);

  const [search, setSearch] = useState('');
  const [filterSpaceId, setFilterSpaceId] = useState<string | 'all' | 'unorganized'>('all');
  // Open threads are the tabs a person is working in; closed ones are the
  // archive (#181). Both are reachable here until the shell's tab strip
  // (#182) takes over the open ones.
  const [filterState, setFilterState] = useState<'all' | 'open' | 'starred' | 'closed'>('all');
  const [sortOrder, setSortOrder] = useState<'newest' | 'oldest'>('newest');

  // Three-dot menu
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [menuThreadId, setMenuThreadId] = useState<string | null>(null);

  // Rename dialog
  const [renameOpen, setRenameOpen] = useState(false);
  const [renameId, setRenameId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');

  // Add to Space dialog
  const [spaceOpen, setSpaceOpen] = useState(false);
  const [spaceThreadId, setSpaceThreadId] = useState<string | null>(null);
  const [newSpaceName, setNewSpaceName] = useState('');

  // Delete confirmation
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  const filtered = useMemo(() => {
    let list = savedThreads;
    if (filterState === 'open') list = list.filter(isOpenThread);
    else if (filterState === 'starred') list = list.filter((t) => isOpenThread(t) && t.starred);
    else if (filterState === 'closed') list = list.filter((t) => !isOpenThread(t));
    // A thread whose space no longer exists (deleted, or its unassign failed) is unorganized.
    if (filterSpaceId === 'unorganized') list = list.filter((t) => !t.spaceId || !spaces.some((s) => s.id === t.spaceId));
    else if (filterSpaceId !== 'all') list = list.filter((t) => t.spaceId === filterSpaceId);
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter((t) => t.title.toLowerCase().includes(q) || t.preview.toLowerCase().includes(q));
    }
    return [...list].sort((a, b) => {
      const aT = new Date(a.updatedAt).getTime();
      const bT = new Date(b.updatedAt).getTime();
      return sortOrder === 'newest' ? bT - aT : aT - bT;
    });
  }, [savedThreads, spaces, search, filterSpaceId, filterState, sortOrder]);

  const openMenu = (e: React.MouseEvent<HTMLElement>, threadId: string) => {
    e.stopPropagation();
    setMenuAnchor(e.currentTarget);
    setMenuThreadId(threadId);
  };
  const closeMenu = () => { setMenuAnchor(null); setMenuThreadId(null); };

  const handleRenameOpen = () => {
    const thread = savedThreads.find((t) => t.id === menuThreadId);
    if (!thread) return;
    setRenameId(thread.id);
    setRenameValue(thread.title);
    setRenameOpen(true);
    closeMenu();
  };

  const handleRenameSubmit = () => {
    if (renameId && renameValue.trim()) renameThread(renameId, renameValue.trim());
    setRenameOpen(false);
    setRenameId(null);
  };

  const handleDeleteOpen = () => {
    setDeleteConfirmId(menuThreadId);
    closeMenu();
  };

  const menuThread = menuThreadId ? savedThreads.find((t) => t.id === menuThreadId) : null;

  const handleCloseOrReopen = () => {
    if (menuThread) {
      if (isOpenThread(menuThread)) closeThread(menuThread.id);
      else reopenThread(menuThread.id);
    }
    closeMenu();
  };

  const handleDeleteConfirm = () => {
    if (deleteConfirmId) deleteThread(deleteConfirmId);
    setDeleteConfirmId(null);
  };

  const handleAddToSpaceOpen = () => {
    setSpaceThreadId(menuThreadId);
    setNewSpaceName('');
    setSpaceOpen(true);
    closeMenu();
  };

  const handleAddToExistingSpace = (spaceId: string) => {
    if (spaceThreadId) addThreadToSpace(spaceThreadId, spaceId);
    setSpaceOpen(false);
    setSpaceThreadId(null);
  };

  const handleRemoveFromSpace = () => {
    if (spaceThreadId) addThreadToSpace(spaceThreadId, null);
    setSpaceOpen(false);
    setSpaceThreadId(null);
  };

  const handleCreateSpaceAndAdd = () => {
    if (!newSpaceName.trim() || !spaceThreadId) return;
    const newSpace = createSpace(newSpaceName.trim());
    addThreadToSpace(spaceThreadId, newSpace.id);
    setSpaceOpen(false);
    setSpaceThreadId(null);
    setNewSpaceName('');
  };

  const activeThread = spaceThreadId ? savedThreads.find((t) => t.id === spaceThreadId) : null;

  return (
    <Box sx={{ height: '100%', overflow: 'hidden', display: 'flex', flexDirection: 'column', bgcolor: 'background.default' }}>
      {/* Hero — mirrors BrowsePage pattern */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'stretch',
          pt: 3,
          pb: 3,
          px: { xs: 2, sm: 4 },
          bgcolor: 'background.paper',
          borderBottom: 1,
          borderColor: 'divider',
          flexShrink: 0,
          '& > *': { width: '100%', maxWidth: 880, mx: 'auto' },
        }}
      >
        {/* Heading */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
          <HistoryIcon sx={{ color: 'primary.main', fontSize: 26 }} />
          <Typography component="h1" variant="h4" sx={{ fontWeight: 700, color: 'text.primary' }}>
            History
          </Typography>
        </Box>
        <Typography variant="h6" color="text.secondary" sx={{ mb: 3, fontWeight: 400, textAlign: 'left', fontSize: 15 }}>
          Your threads — click any one to pick up where you left off.
        </Typography>

        {/* Search bar — same Paper style as BrowsePage */}
        <Box sx={{ width: '100%', maxWidth: 880 }}>
          <Paper
            elevation={0}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              px: 2,
              py: 1,
              borderRadius: 3,
              border: '1px solid',
              borderColor: 'divider',
              mb: 1.5,
            }}
          >
            <SearchIcon sx={{ color: 'text.secondary', flexShrink: 0 }} />
            <TextField
              variant="standard"
              fullWidth
              placeholder="Search your threads..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{ disableUnderline: true }}
              sx={{ '& input': { fontSize: 15, lineHeight: 1.5 } }}
            />
          </Paper>

          {/* Filter + sort row — same max-width as search bar */}
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
            {([
              ['all', 'All'],
              ['open', 'Open'],
              ['starred', 'Starred'],
              ['closed', 'Closed'],
            ] as const).map(([key, label]) => (
              <Chip
                key={key}
                label={label}
                size="small"
                icon={key === 'starred' ? <StarIcon sx={{ fontSize: 14 }} /> : undefined}
                variant={filterState === key ? 'filled' : 'outlined'}
                color={filterState === key ? 'primary' : 'default'}
                onClick={() => setFilterState(key)}
                sx={{ cursor: 'pointer' }}
              />
            ))}
            {spaces.length > 0 && <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />}
            {spaces.length > 0 && (
            <Chip
              label="All spaces"
              size="small"
              variant={filterSpaceId === 'all' ? 'filled' : 'outlined'}
              color={filterSpaceId === 'all' ? 'primary' : 'default'}
              onClick={() => setFilterSpaceId('all')}
              sx={{ cursor: 'pointer' }}
            />
            )}
            {spaces.length > 0 && (
              <Chip
                label="Unorganized"
                size="small"
                variant={filterSpaceId === 'unorganized' ? 'filled' : 'outlined'}
                color={filterSpaceId === 'unorganized' ? 'primary' : 'default'}
                onClick={() => setFilterSpaceId('unorganized')}
                sx={{ cursor: 'pointer' }}
              />
            )}
            {spaces.map((sp) => (
              <Chip
                key={sp.id}
                icon={<FolderIcon sx={{ fontSize: 14 }} />}
                label={sp.name}
                size="small"
                variant={filterSpaceId === sp.id ? 'filled' : 'outlined'}
                color={filterSpaceId === sp.id ? 'primary' : 'default'}
                onClick={() => setFilterSpaceId(sp.id)}
                sx={{ cursor: 'pointer' }}
              />
            ))}
            <Box sx={{ flex: 1 }} />
            <Chip
              label={sortOrder === 'newest' ? 'Sort: Newest' : 'Sort: Oldest'}
              size="small"
              variant="outlined"
              onClick={() => setSortOrder((s) => (s === 'newest' ? 'oldest' : 'newest'))}
              sx={{ cursor: 'pointer' }}
            />
          </Box>
        </Box>
      </Box>

      {/* Thread list — scrollable, content centered at maxWidth 720 */}
      <Box sx={{ flex: 1, overflow: 'auto', py: 2 }}>
        <Box sx={{ maxWidth: 880, mx: 'auto', px: 2 }}>
        {filtered.length === 0 ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 300, color: 'text.secondary' }}>
            <HistoryIcon sx={{ fontSize: 48, opacity: 0.2, mb: 2 }} />
            <Typography variant="body2">
              {search.trim()
                ? 'No threads match your search.'
                : filterState === 'closed'
                  ? 'Nothing closed yet. Close a thread and it will wait here until you reopen it.'
                  : 'No conversation history yet. Start a chat on the landing page!'}
            </Typography>
          </Box>
        ) : (
          <>
            {filtered.map((thread) => {
              const space = thread.spaceId ? spaces.find((s) => s.id === thread.spaceId) : null;
              return (
                <Box
                  key={thread.id}
                  onClick={() => loadThread(thread.id)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => { if (e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); loadThread(thread.id); } }}
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 1,
                    py: 2,
                    px: 2,
                    mx: 0,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    cursor: 'pointer',
                    borderRadius: 1,
                    '&:hover': { bgcolor: 'action.hover' },
                    transition: 'background 0.12s',
                    '&:focus-visible': { outline: '2px solid var(--accent)', outlineOffset: -2 },
                    '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
                  }}
                >
                  {/* Thread content */}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.25 }}>
                      <Typography
                        variant="body1"
                        fontWeight={500}
                        noWrap
                        sx={{ color: isOpenThread(thread) ? 'primary.main' : 'text.secondary', flex: 1 }}
                      >
                        {thread.title}
                      </Typography>
                      {!isOpenThread(thread) && (
                        <Chip
                          label="Closed"
                          size="small"
                          variant="outlined"
                          sx={{ height: 20, fontSize: '0.7rem', flexShrink: 0, '& .MuiChip-label': { px: 0.75 } }}
                        />
                      )}
                      {space && (
                        <Chip
                          icon={<FolderIcon sx={{ fontSize: 12 }} />}
                          label={space.name}
                          size="small"
                          variant="outlined"
                          sx={{ height: 20, fontSize: '0.7rem', flexShrink: 0, '& .MuiChip-label': { px: 0.75 } }}
                        />
                      )}
                    </Box>
                    {thread.preview && (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          mb: 0.5,
                        }}
                      >
                        {thread.preview}
                      </Typography>
                    )}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <AccessTimeIcon sx={{ fontSize: 13, color: 'text.disabled' }} />
                      <Typography variant="caption" color="text.disabled">
                        {formatRelativeTime(thread.updatedAt)}
                      </Typography>
                      {thread.detectedPerspective && (
                        <>
                          <Typography variant="caption" color="text.disabled" sx={{ mx: 0.25 }}>·</Typography>
                          <ChatBubbleOutlineIcon sx={{ fontSize: 12, color: 'text.disabled' }} />
                          <Typography variant="caption" color="text.disabled">
                            {thread.detectedPerspective.replace(/_/g, ' ')}
                          </Typography>
                        </>
                      )}
                    </Box>
                  </Box>

                  {/* Star — the thread survives at the top of "Continue where you left off" */}
                  <Tooltip title={thread.starred ? 'Unstar' : 'Star'}>
                    <IconButton
                      size="small"
                      aria-label={thread.starred ? 'Unstar thread' : 'Star thread'}
                      onClick={(e) => { e.stopPropagation(); starThread(thread.id, !thread.starred); }}
                      sx={{ color: thread.starred ? 'warning.main' : 'text.disabled', '&:hover': { color: 'warning.main' }, flexShrink: 0, mt: 0.25 }}
                    >
                      {thread.starred ? <StarIcon fontSize="small" /> : <StarBorderIcon fontSize="small" />}
                    </IconButton>
                  </Tooltip>

                  {/* Three-dot menu button — always visible but subtle */}
                  <IconButton
                    size="small"
                    aria-label="Thread actions"
                    onClick={(e) => openMenu(e, thread.id)}
                    sx={{ color: 'text.disabled', '&:hover': { color: 'text.primary' }, flexShrink: 0, mt: 0.25 }}
                  >
                    <MoreHorizIcon fontSize="small" />
                  </IconButton>
                </Box>
              );
            })}
          </>
        )}
        </Box>
      </Box>

      {/* Three-dot context menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={closeMenu}
        sx={{ '& .MuiPaper-root': { minWidth: 180 } }}
      >
        <MenuItem onClick={handleAddToSpaceOpen} sx={{ gap: 1.5 }}>
          <AddIcon fontSize="small" />
          <Typography variant="body2">Add to Space</Typography>
        </MenuItem>
        <MenuItem onClick={handleRenameOpen} sx={{ gap: 1.5 }}>
          <EditIcon fontSize="small" />
          <Typography variant="body2">Rename Thread</Typography>
        </MenuItem>
        <MenuItem onClick={handleCloseOrReopen} sx={{ gap: 1.5 }}>
          {menuThread && !isOpenThread(menuThread) ? <RestoreIcon fontSize="small" /> : <CloseIcon fontSize="small" />}
          <Typography variant="body2">
            {menuThread && !isOpenThread(menuThread) ? 'Reopen Thread' : 'Close Thread'}
          </Typography>
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleDeleteOpen} sx={{ gap: 1.5, color: 'error.main' }}>
          <DeleteIcon fontSize="small" />
          <Typography variant="body2" color="error">Delete</Typography>
        </MenuItem>
      </Menu>

      {/* Rename dialog */}
      <Dialog open={renameOpen} onClose={() => setRenameOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Rename Thread</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            size="small"
            label="Thread title"
            value={renameValue}
            onChange={(e) => setRenameValue(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleRenameSubmit()}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRenameOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleRenameSubmit} disabled={!renameValue.trim()}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirmation dialog */}
      <Dialog open={Boolean(deleteConfirmId)} onClose={() => setDeleteConfirmId(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Thread?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            This thread will be permanently deleted and cannot be undone. To keep it out of the way but recoverable, close it instead.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmId(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDeleteConfirm}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add to Space dialog */}
      <Dialog open={spaceOpen} onClose={() => setSpaceOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Add to Space</DialogTitle>
        <DialogContent>
          {spaces.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                Existing spaces
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                {spaces.map((sp) => {
                  const isCurrent = activeThread?.spaceId === sp.id;
                  return (
                    <Button
                      key={sp.id}
                      variant={isCurrent ? 'contained' : 'outlined'}
                      size="small"
                      startIcon={<FolderIcon />}
                      onClick={() => handleAddToExistingSpace(sp.id)}
                      sx={{ justifyContent: 'flex-start', textTransform: 'none' }}
                    >
                      {sp.name}
                      {isCurrent && (
                        <Typography variant="caption" sx={{ ml: 'auto', opacity: 0.7 }}>
                          current
                        </Typography>
                      )}
                    </Button>
                  );
                })}
                {activeThread?.spaceId && (
                  <Button
                    variant="text"
                    size="small"
                    onClick={handleRemoveFromSpace}
                    sx={{ justifyContent: 'flex-start', textTransform: 'none', color: 'text.secondary' }}
                  >
                    Remove from space
                  </Button>
                )}
              </Box>
              <Divider sx={{ my: 1.5 }} />
            </Box>
          )}
          <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
            Create new space
          </Typography>
          <TextField
            autoFocus={spaces.length === 0}
            fullWidth
            size="small"
            placeholder="Space name (e.g. Revenue Analysis)"
            value={newSpaceName}
            onChange={(e) => setNewSpaceName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleCreateSpaceAndAdd()}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSpaceOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreateSpaceAndAdd} disabled={!newSpaceName.trim()}>
            Create & Add
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
